
import React, { useState } from "react";
import { Editor } from "@tiptap/react";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";

// Import our extracted components
import PlatformSelector from "./video/PlatformSelector";
import VideoUrlInput from "./video/VideoUrlInput";
import CaptionInput from "./video/CaptionInput";
import SubmitButton from "./video/SubmitButton";
import { getVideoId } from "./video/VideoEmbedUtils";

interface VideoFormProps {
  editor: Editor;
  onClose: () => void;
}

const VideoForm = ({ editor, onClose }: VideoFormProps) => {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [error, setError] = useState("");
  const [platform, setPlatform] = useState<'youtube' | 'vimeo'>('youtube');
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsProcessing(true);

    try {
      // Validate URL
      if (!url) {
        setError("Please enter a video URL");
        setIsProcessing(false);
        return;
      }

      // Get video ID
      const videoId = getVideoId(url, platform);
      if (!videoId) {
        setError(`Invalid ${platform} URL. Please check the URL and try again.`);
        setIsProcessing(false);
        return;
      }

      console.log(`Embedding ${platform} video with ID: ${videoId}`);
      
      try {
        // Use the video extension command
        editor
          .chain()
          .focus()
          .setVideo({
            src: videoId,
            platform: platform,
            caption: caption || undefined,
          })
          .run();
        
        toast({
          title: "Success",
          description: "Video embedded successfully"
        });

        onClose();
      } catch (embedError: any) {
        console.error("Error in editor command:", embedError);
        setError("Failed to embed video. Please try again.");
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error("Error embedding video:", error);
      setError(error.message || "An unexpected error occurred");
      toast({
        variant: "destructive",
        title: "Error embedding video",
        description: error.message || "Failed to embed video"
      });
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-1">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <PlatformSelector platform={platform} setPlatform={setPlatform} />
      <VideoUrlInput platform={platform} url={url} setUrl={setUrl} />
      <CaptionInput caption={caption} setCaption={setCaption} />
      <SubmitButton isProcessing={isProcessing} />
    </form>
  );
};

export default VideoForm;
